import React, { useCallback, useState } from 'react';
import { styled } from '@mui/material';
import { Grid } from '@mui/system';
import { DatePicker, MobileDatePicker } from '@mui/x-date-pickers';
import { isEmpty } from 'lodash';
import CompanyList from 'components/MeasurementDates/components/company-list/CompanyList';
import FundList from 'components/MeasurementDates/components/fund-list/FundList';
import { useMDContext } from 'context/MDContext';
import { gridShortDate, nextQuarterEndDate } from 'utilities/dateFormats';
import { CompaniesSelectorProps } from './CompaniesSelector.props';
import { FullWidthDatePicker, MarginTopGrid } from './CompaniesSelector.styles';
import { SuggestedDate } from './suggested-date';
import getSuggestedQuarterFiscalEndDate from '../utilities/getSuggestedQuarterFiscalEndDate';

const CompaniesSelector = ({ activeCompany }: CompaniesSelectorProps) => {
  const {
    measurementDate,
    setMeasurementDate,
    errors,
    items,
    companyList,
    isLoading,
    setSelectedFunds,
    selectedFunds,
    selectedCompanies,
    setSelectedCompanies,
  } = useMDContext();
  const [date, setDate] = useState<Date | null>(measurementDate ? new Date(measurementDate) : null);

  const nextQuarterEnd = nextQuarterEndDate();
  let suggestedQuarterFiscalEnd = '';
  if (activeCompany) {
    suggestedQuarterFiscalEnd = getSuggestedQuarterFiscalEndDate(activeCompany.fiscal_year_end ?? {});
  }
  const handleChange = useCallback((date: Date | null) => {
    setDate(date);
  }, []);

  const handleAccept = (date: Date | null) => {
    if (date) {
      setMeasurementDate(gridShortDate(date));
    }
  };

  const handleFocus = () => {
    if (!date) {
      setDate(new Date());
    }
  };

  const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    const date = new Date(event.target.value);
    if (measurementDate !== event.target.value) {
      handleAccept(date);
    }
  };

  const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      const date = new Date(e.currentTarget.value);
      handleAccept(date);
    }
  };

  const handleClickSuggested = (suggestedDate: string) => {
    const date = new Date(suggestedDate);
    setDate(date);
    handleAccept(date);
  };

  return (
    <>
      <Grid size={{ xs: 6 }}>
        <FullWidthDatePicker
          value={date}
          format="MM/dd/yyyy"
          onChange={handleChange}
          onAccept={handleAccept}
          openTo={'day'}
          views={['year', 'month', 'day']}
          label="Measurement Date"
          slotProps={{
            textField: {
              variant: 'standard',
              margin: 'normal',
              id: 'new-measurement-date',
              name: 'new-measurement-date',
              onFocus: handleFocus,
              onBlur: handleBlur,
              onKeyDown,
              helperText: errors.date,
              error: !!errors.date,
              placeholder: 'SELECT DATE',
            },
          }}
        />
        {!isEmpty(suggestedQuarterFiscalEnd) ? (
          <SuggestedDate suggestedDate={suggestedQuarterFiscalEnd} handleClickSuggested={handleClickSuggested} />
        ) : (
          <SuggestedDate suggestedDate={nextQuarterEnd} handleClickSuggested={handleClickSuggested} />
        )}
      </Grid>
      <MarginTopGrid container spacing={4}>
        <Grid size={{ xs: 6, md: 6 }}>
          <FundList
            items={items}
            isLoading={isLoading}
            setSelectedCompanies={setSelectedCompanies}
            selectedCompanies={selectedCompanies}
            setSelectedFunds={setSelectedFunds}
            selectedFunds={selectedFunds}
            isDeletingMD={false}
          />
        </Grid>
        <Grid size={{ xs: 6, md: 6 }}>
          <CompanyList
            items={items}
            companyList={companyList}
            isLoading={isLoading}
            setSelectedCompanies={setSelectedCompanies}
            selectedCompanies={selectedCompanies}
            isDeletingMD={false}
          />
        </Grid>
      </MarginTopGrid>
    </>
  );
};

export default CompaniesSelector;
